import http from './http';
import {
    GetInterviewFieldDefinitions,
    CreateInterviewFieldDefinition,
    UpdateInterviewFieldDefinition,
    DeleteInterviewFieldDefinition,
    UploadInterviewFieldFile,
} from './Api';

// Admin-defined extra fields on the Schedule Interview form.
// A definition is { id, label, fieldKey, fieldType, options, isRequired, displayOrder }.
//
// fieldKey is generated SERVER-SIDE from the label and is stable for the life of
// the field, even if the label is later edited. Never derive it on the client —
// every answer is keyed by it, and an unknown key is rejected by the API.

export const FIELD_TYPES = [
    { key: 'Text', label: 'Text' },
    { key: 'Number', label: 'Number' },
    { key: 'Date', label: 'Date' },
    { key: 'Dropdown', label: 'Dropdown' },
    { key: 'MultiSelect', label: 'Multi-select' },
    { key: 'YesNo', label: 'Yes / No' },
    { key: 'File', label: 'File upload' },
];

// Types that carry a saved list of choices.
export const HAS_OPTIONS = new Set(['Dropdown', 'MultiSelect']);

export const MAX_FILE_BYTES = 10 * 1024 * 1024;

// Stored blobs are prefixed with a dedupe hash — "3f2ab6..._resume.pdf". Show
// the name the user actually picked, not the storage artefact.
export const fileNameFromPath = (path) => {
    const last = String(path || '').split('/').pop() || '';
    return last.replace(/^[0-9a-fA-F]{8,}_/, '') || last;
};

export const fmtBytes = (n) => {
    if (!Number.isFinite(n) || n <= 0) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const normalizeField = (f) => ({
    id: f.id,
    label: f.label ?? '',
    fieldKey: f.fieldKey ?? '',
    fieldType: f.fieldType ?? 'Text',
    options: Array.isArray(f.options) ? f.options : [],
    isRequired: Boolean(f.isRequired),
    displayOrder: Number(f.displayOrder) || 0,
});

const sortForDisplay = (list) => [...list].sort((a, b) => a.displayOrder - b.displayOrder);

// The definitions themselves, for the builder screen. The Schedule form takes
// its copy from GetInterviewFormOptions instead, so it stays in step with the
// vacancies and rounds it is rendered beside.
export const listFields = async () => {
    const { data: body } = await http.get(GetInterviewFieldDefinitions);
    if (body?.error) throw new Error(body.message || 'Could not load interview fields.');
    const list = Array.isArray(body?.data) ? body.data : (body?.data?.fields || []);
    return sortForDisplay(list.map(normalizeField));
};

export const createField = async (draft) => {
    const { data: body } = await http.post(CreateInterviewFieldDefinition, {
        label: String(draft.label || '').trim(),
        fieldType: draft.fieldType,
        options: HAS_OPTIONS.has(draft.fieldType) ? draft.options : [],
        isRequired: Boolean(draft.isRequired),
    });
    if (body?.error) throw new Error(body.message || 'Could not add the field.');
    return body?.data ? normalizeField(body.data) : null;
};

// fieldType is immutable server-side, so it is deliberately not sent.
export const updateField = async (draft) => {
    const { data: body } = await http.put(UpdateInterviewFieldDefinition, {
        id: draft.id,
        label: String(draft.label || '').trim(),
        fieldType: draft.fieldType,
        options: HAS_OPTIONS.has(draft.fieldType) ? draft.options : [],
        isRequired: Boolean(draft.isRequired),
    });
    if (body?.error) throw new Error(body.message || 'Could not save the field.');
    return body?.data ? normalizeField(body.data) : null;
};

// Soft delete: new forms stop offering it, answers already on interviews stay.
export const deleteField = async (id) => {
    const { data: body } = await http.delete(DeleteInterviewFieldDefinition, { params: { id } });
    if (body?.error) throw new Error(body.message || 'Could not remove the field.');
};

// Files are staged first and referenced by path, because customFields travels
// inside a JSON body. Returns { path, url } — path is what the form submits.
export const uploadFieldFile = async (fieldKey, file) => {
    const fd = new FormData();
    fd.append('file', file);
    const { data: body } = await http.post(UploadInterviewFieldFile, fd, { params: { fieldKey } });
    if (body?.error) throw new Error(body.message || 'Could not upload the file.');
    return body?.data || null;
};

// ---- form-state helpers -------------------------------------------------

export const emptyValues = (fields) => fields.reduce((acc, f) => {
    if (f.fieldType === 'YesNo') acc[f.fieldKey] = false;
    else if (f.fieldType === 'MultiSelect') acc[f.fieldKey] = [];
    else if (f.fieldType === 'File') acc[f.fieldKey] = null;
    else acc[f.fieldKey] = '';
    return acc;
}, {});

// Update replaces customFields wholesale, so an edit form must start from the
// answers already on the interview or untouched fields would be wiped.
export const valuesFromInterview = (fields, customFields) => {
    const answered = Array.isArray(customFields) ? customFields : [];
    const byKey = answered.reduce((acc, a) => { acc[a.fieldKey] = a.value; return acc; }, {});
    return fields.reduce((acc, f) => {
        const v = byKey[f.fieldKey];
        if (f.fieldType === 'YesNo') acc[f.fieldKey] = v === true || v === 'true';
        else if (f.fieldType === 'MultiSelect') acc[f.fieldKey] = Array.isArray(v) ? v : (v ? [v] : []);
        else if (f.fieldType === 'File') acc[f.fieldKey] = v ? { path: v, name: String(v).split('/').pop() } : null;
        else acc[f.fieldKey] = v ?? '';
        return acc;
    }, {});
};

export const missingRequired = (fields, values) => fields
    .filter((f) => f.isRequired)
    .filter((f) => {
        const v = values?.[f.fieldKey];
        if (f.fieldType === 'YesNo') return v !== true;
        if (f.fieldType === 'MultiSelect') return !Array.isArray(v) || v.length === 0;
        if (f.fieldType === 'File') return !v?.path;
        return !String(v ?? '').trim();
    })
    .map((f) => f.label);

// Only keys the API currently knows about — a stale key is a 400, not a silent
// drop, so sending one from an out-of-date form would fail the whole submit.
export const buildCustomPayload = (fields, values) => fields.reduce((acc, f) => {
    const v = values?.[f.fieldKey];
    if (f.fieldType === 'YesNo') { acc[f.fieldKey] = Boolean(v); return acc; }
    if (f.fieldType === 'MultiSelect') { if (Array.isArray(v) && v.length) acc[f.fieldKey] = v; return acc; }
    if (f.fieldType === 'File') { if (v?.path) acc[f.fieldKey] = v.path; return acc; }
    if (String(v ?? '').trim()) acc[f.fieldKey] = v;
    return acc;
}, {});
